// CreateProjectsPage.tsx

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faPlus,
  faTimes,
  faLayerGroup,
  faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';
import SectionCard from '@frontend/Components/SectionCard';
import FormInput from '@frontend/Components/Forms/FormInput/FormInput';
import FormSelect from '@frontend/Components/Forms/FormSelect/FormSelect';
import FormEntryMultilineTextbox from '@frontend/Components/Forms/Entry/MultilineTextbox/FormEntryMultilineTextbox';
import TeamMembersPicker from '@frontend/Components/TeamMembersPicker';
import FileDropzone from '@frontend/Components/FileDropzone';
import CustomFieldsTablesPanel from '@frontend/Components/CustomFieldsTablesPanel';
import AutoGenerateButton from '@frontend/Components/AutoGenerateButton';
import ClientService from '../../AppSettings/services/ClientService';
import AgentsService from '../../AppSettings/services/AgentsService';
import EmployeeService from '../../AppSettings/services/EmployeeService';
import WorkflowTemplateService from '../../AppSettings/services/WorkflowTemplateService';
import CustomFieldsService from '../../AppSettings/services/CustomfieldsService';
import './CreateProjectsPage.css';
import SelectModal from '../../Components/SelectModal/SelectModal';
import TablesService from '../../AppSettings/services/TablesService';
import { MatterFormData } from '../../Models/Matter/MatterModels';
import { SelectOption } from '../../Models/Form/FormModels';
import { Client } from '../../Models/Client/ClientModels';
import { Employees } from '../../Models/Employee/EmployeeModels';
import { CustomField } from '../../Models/CustomField/CustomFieldModels';
import { GroupRule } from '../../Models/GroupRule/GroupRuleModels';
import { CustomTable } from '../../Models/Table/CustomTableModels';
import { TemplateListItem } from '@frontend/types/workflows';

import MatterService from '../../AppSettings/services/MatterService';
import MatterCostCodeBudgetService from '../../AppSettings/services/MatterCostCodeBudgetService';
import CostCodeBudgetInputs from '../../Financial/CostCodeBudgets/CostCodeBudgetInputs';
import TemplateEdit from '../../Profile/Components/Eng_templates/TemplateEdit';
import '../../Profile/Components/Eng_templates/TemplateEdit.css';
interface ProjectFormData extends MatterFormData {
  projectId: string;
  matterid: string;
  po_number: string;
}

type TemplateStepSummary = {
  stepId: string;
  name: string;
  stepOrder: number;
  discipline?: string | null;
  offsetType?: string;
  offsetDays?: number;
};

const CreateProjectsPage = () => {
  const navigate = useNavigate();
  // State Variables
  const [clients, setClients] = useState<SelectOption[]>([]);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [templateStepsById, setTemplateStepsById] = useState<Record<string, TemplateStepSummary[]>>({});
  const [selectedClientID, setSelectedClientID] = useState<string>(''); // Store selected client ID
  const [showWorkflows, setShowWorkflows] = useState<boolean>(false);
  const [showMoreInfoModal, setShowMoreInfoModal] = useState<boolean>(false);
  const [loadingWorkflows, setLoadingWorkflows] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showEmployeeModal, setShowEmployeeModal] = useState<boolean>(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([]);
  const [showSuccessBanner, setShowSuccessBanner] = useState<boolean>(false);
  const [showCustomFieldsModal, setShowCustomFieldsModal] = useState<boolean>(false);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [selectedCustomFields, setSelectedCustomFields] = useState<Record<number, boolean>>({});
  const [groupRules, setGroupRules] = useState<GroupRule[]>([]);
  const [showGroupRules, setShowGroupRules] = useState<boolean>(false);
  const [selectedGroupRule, setSelectedGroupRule] = useState<GroupRule | null>(null);
  const [showCustomTablesModal, setShowCustomTablesModal] = useState<boolean>(false);
  const [customTables, setCustomTables] = useState<CustomTable[]>([]);
  const [selectedCustomTables, setSelectedCustomTables] = useState<number[]>([]);

  // React Query Hooks for Data Fetching
  const clientData = useQuery({
    queryKey: ['Clients'],
    queryFn: ClientService.getClients,
  });

  const agentData = useQuery({
    queryKey: ['Agents'],
    queryFn: AgentsService.getAgents,
  });

  const employeeData = useQuery({
    queryKey: ['Employees'],
    queryFn: EmployeeService.getEmployees,
  });

  // State for form inputs
  const [formData, setFormData] = useState<ProjectFormData>({
    mattername: '',
    mattertype: '',
    projectId: '', // User-inputted Project ID portion
    clientid: '', // Selected Client ID
    matterid: '', // Concatenated ProjectID-ClientID
    employeeid: '',
    opendate: '',
    closedate: '',
    description: '',
    amount: '0',
    budget: '',
    po_number: '',
    workflowid: '',
    country: '',
    application_date: '',
    application_no: '',
    registration_date: '',
    registration_no: '',
    publication_date: '',
    publication_no: '',
    application_status: '',
    priority_data: '',
    office_action_date: '',
    expiry_date: '',
    assoc_employees: [],
    assoc_customfields: [],
  });

  // Per-cost-code hour budgets keyed by code (string values so a blank field stays blank/uncapped).
  const [codeBudgets, setCodeBudgets] = useState<Record<string, string>>({});

  // Populate Add Client dropdown (clients tagged "org" + agents tagged "individual") and employees
  useEffect(() => {
    const clientOptions: (SelectOption & { tag?: 'org' | 'individual' })[] = [];
    if (clientData?.data) {
      clientData.data.forEach((client: Client) => {
        const name = [client.FirstName, client.LastName].filter(Boolean).join(' ').trim() || '—';
        const company = client.Company ? ` (${client.Company})` : '';
        clientOptions.push({
          value: client.ClientID,
          label: `${client.ClientID} - ${name}${company}`,
          tag: 'org',
        });
      });
    }
    // A6: the "Add Client" picker must offer CLIENTS only. Agents are non-clients (vendors/outside
    // parties, kept in the `agents` table); offering them here let a project attach to a non-client,
    // corrupting downstream client-keyed flows (invoicing, billing, client filters).
    if (clientOptions.length > 0) {
      setClients(clientOptions);
    }

    if (employeeData?.data) {
      setEmployees(
        employeeData.data.map((employee: Employees) => ({
          value: employee.EmployeeID,
          label: `${employee.FirstName} ${employee.LastName}`,
        }))
      );
    }

    if (clientData.error || employeeData.error || agentData.error) {
      setError('Failed to load clients, agents, or employees.');
    }
  }, [
    clientData?.data,
    agentData?.data,
    employeeData?.data,
    clientData?.error,
    employeeData?.error,
    agentData?.error,
  ]);

  // Update matterid when projectId or clientid changes
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      matterid:
        prevData.projectId && selectedClientID
          ? `${prevData.projectId}-${selectedClientID}`
          : '',
    }));
  }, [formData.projectId, selectedClientID]);

  // Update assoc_employees when team members change
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      assoc_employees: selectedTeamMembers,
    }));
  }, [selectedTeamMembers]);

  // Auto-populate Project ID from Job Number
  useEffect(() => {
    if (formData.application_no) {
      setFormData((prevData) => ({
        ...prevData,
        projectId: formData.application_no,
      }));
    }
  }, [formData.application_no]);

  // Derive Project Name from Area + LSD + AFE
  useEffect(() => {
    const parts = [
      formData.country?.trim(),
      formData.registration_no?.trim(),
      formData.publication_no?.trim(),
    ].filter(Boolean);
    const derivedName = parts.join(' - ');
    setFormData((prev) => ({
      ...prev,
      mattername: derivedName,
    }));
  }, [formData.country, formData.registration_no, formData.publication_no]);

  // Handle Input Changes for Text and Date Inputs
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Fetch templates and their steps for the workflow picker
  const fetchTemplates = async () => {
    try {
      setLoadingWorkflows(true);
      const response = await WorkflowTemplateService.listWorkflowTemplates({ limit: 100 });
      const templateList = response.data?.templates ?? [];
      setTemplates(templateList);

      const stepsEntries = await Promise.all(
        templateList.map(async (tpl) => {
          try {
            const detail = await WorkflowTemplateService.getWorkflowTemplate(tpl.templateId);
            const steps = detail.data?.steps ?? [];
            const normalized: TemplateStepSummary[] = steps.map((step) => ({
              stepId: step.stepId,
              name: step.name,
              stepOrder: step.stepOrder ?? step.order ?? 0,
              discipline: step.discipline,
              offsetType: step.offsetType,
              offsetDays: step.offsetDays,
            })).sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0));
            return [tpl.templateId, normalized] as const;
          } catch (innerErr) {
            console.error('Error loading template steps:', innerErr);
            return [tpl.templateId, []] as const;
          }
        })
      );

      setTemplateStepsById(Object.fromEntries(stepsEntries));
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates.');
    } finally {
      setLoadingWorkflows(false);
    }
  };

  // Ensure template options populate the dropdown on load
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Toggle Modals for More Information and Workflows
  const toggleModals = async () => {
    if (!showMoreInfoModal) {
      await fetchTemplates(); // Only fetch templates when opening
    }
    setShowMoreInfoModal((prev) => !prev);
  };

  // Toggle Workflow Modal
  const toggleWorkflowModal = async () => {
    if (!showWorkflows) {
      await fetchTemplates(); // Only fetch templates when opening
    }
    setShowWorkflows((prev) => !prev);
  };

  // Toggle Employee Modal for Adding Team Members
  const handleAddTeamMembersClick = () => {
    setShowEmployeeModal((prev) => !prev);
  };

  // Handle Workflow Selection
  const handleWorkflowClick = (templateId: string) => {
    setFormData((prevData) => ({
      ...prevData,
      workflowid: templateId,
    }));
  };

  // Handle Form Submission
  const handleSubmit = () => {
    // Basic Validation
    if (
      !formData.mattername ||
      !formData.clientid ||
      !formData.employeeid ||
      !formData.projectId
    ) {
      setError(
        'Please fill in all required fields (Project Name, Project ID, Client, Assign Leader).'
      );
      return;
    }

    if (!formData.matterid) {
      setError('Matter ID is required.');
      return;
    }

    // The overall project budget is DERIVED as the sum of the per-cost-code budgets. It is set by
    // the admin-gated PUT below (which re-syncs matters.budget server-side), so we do NOT seed an
    // independent budget here — a non-admin creator can't persist code rows, so their matters.budget
    // correctly stays unset/uncapped rather than showing a total with no backing breakdown.
    createProjectMutation.mutate({ ...formData, budget: '' });
  };

  // Fetch custom fields
  const fetchCustomFields = async () => {
    try {
      const response = await CustomFieldsService.getCustomFields();
      setCustomFields(response?.data || []);
    } catch (err) {
      console.error('Error fetching custom fields:', err);
      setError('Failed to load custom fields.');
    }
  };

  // Fetch group rules
  const fetchGroupRules = async () => {
    try {
      const response = await CustomFieldsService.getGroupRules();
      setGroupRules(response);
    } catch (err) {
      console.error('Error fetching group rules:', err);
      setError('Failed to load group rules.');
    }
  };

  // Handle custom fields modal
  const handleCustomFieldsClick = async () => {
    if (!showCustomFieldsModal) {
      await Promise.all([fetchCustomFields(), fetchGroupRules()]);
    }
    setShowCustomFieldsModal(!showCustomFieldsModal);
  };

  // Toggle custom field selection
  const toggleCustomFieldSelection = (fieldId: number) => {
    const newSelectedFields = {
      ...selectedCustomFields,
      [fieldId]: !selectedCustomFields[fieldId],
    };
    setSelectedCustomFields(newSelectedFields);

    // Update formData with selected custom fields as array
    const selectedFieldsArray = Object.keys(newSelectedFields)
      .filter((id) => newSelectedFields[parseInt(id)])
      .map((id) => parseInt(id));

    setFormData((prev) => ({
      ...prev,
      assoc_customfields: selectedFieldsArray,
    }));
  };

  // Handle group rule selection
  const handleGroupRuleSelect = (groupRule: GroupRule) => {
    setSelectedGroupRule(groupRule);
    const newSelectedFields: Record<number, boolean> = {};

    // Handle both array format and object format of assoc_customfields
    let selectedFieldsArray: number[];
    if (Array.isArray(groupRule.assoc_customfields)) {
      selectedFieldsArray = groupRule.assoc_customfields;
    } else {
      selectedFieldsArray = Object.keys(groupRule.assoc_customfields).map(
        (id) => parseInt(id)
      );
    }

    // Update UI state
    selectedFieldsArray.forEach((fieldId) => {
      newSelectedFields[fieldId] = true;
    });

    setSelectedCustomFields(newSelectedFields);
    setFormData((prev) => ({
      ...prev,
      assoc_customfields: selectedFieldsArray,
    }));
  };

  // Function to fetch custom tables (can be called from TemplateEdit)
  const fetchCustomTables = async () => {
    try {
      const response = await TablesService.getCustomTables() as CustomTable[] | { data?: CustomTable[] };
      const list: CustomTable[] = Array.isArray(response) ? response : (response?.data ?? []);
      setCustomTables(list);
      // Select all tables by default
      setSelectedCustomTables(list.map((table) => table.id));
    } catch (err) {
      console.error('Error fetching custom tables:', err);
      setError('Failed to load custom tables.');
    }
  };

  // Add this after other useEffect hooks
  useEffect(() => {
    // Fetch tables on component mount, not just when modal opens
    fetchCustomTables();
  }, []); // Empty dependency array means this runs once on mount

  // Add this handler
  const handleCustomTableClick = (tableId: number) => {
    setSelectedCustomTables((prevSelected) => {
      if (prevSelected.includes(tableId)) {
        return prevSelected.filter((id) => id !== tableId);
      } else {
        return [...prevSelected, tableId];
      }
    });
  };

  // Derive selected custom field IDs for unified panels
  const selectedFieldIds = useMemo(
    () =>
      Object.keys(selectedCustomFields)
        .filter((key) => selectedCustomFields[parseInt(key)])
        .map((key) => parseInt(key)),
    [selectedCustomFields]
  );

  // Mutation for creating project
  const createProjectMutation = useMutation({
    mutationFn: async (formData: ProjectFormData) => {
      // Prepare the payload
      const payload = {
        matterid: formData.matterid, // Include 'matterid' in the payload
        mattername: formData.mattername,
        clientid: formData.clientid,
        employeeid: formData.employeeid,
        opendate: formData.opendate,
        closedate: formData.closedate,
        amount: parseFloat(formData.amount),
        description: formData.description,
        workflowid: formData.workflowid,
        budget: formData.budget || undefined,
        po_number: formData.po_number.trim() || null,
        country: formData.country || undefined,
        application_date: formData.application_date || null,
        application_no: formData.application_no || null,
        registration_date: formData.registration_date || null,
        registration_no: formData.registration_no || null,
        publication_date: formData.publication_date || null,
        publication_no: formData.publication_no || null,
        application_status: formData.application_status || null,
        priority_data: formData.priority_data || null,
        office_action_date: formData.office_action_date || null,
        expiry_date: formData.expiry_date || null,
        assoc_employees: formData.assoc_employees,
        assoc_customfields: formData.assoc_customfields,
        selected_custom_tables: selectedCustomTables,
      };

      const response = await MatterService.createProjectMatter(payload);
      return response;
    },
    onSuccess: async (data: any, variables) => {
      console.log('Project created successfully:', data);
      // Persist per-cost-code budgets against the new project before resetting the form. The id is
      // known up-front for eng projects (variables.matterid) but prefer the server echo. Blank/0
      // fields are skipped (uncapped); budget writes are admin-only server-side (non-fatal here).
      const newMatterId = data?.matterid || variables.matterid;
      const budgetRows = Object.entries(codeBudgets)
        .map(([cost_code, raw]) => ({ cost_code, budget_hours: parseFloat(String(raw)) }))
        .filter((r) => Number.isFinite(r.budget_hours) && r.budget_hours > 0);
      if (newMatterId && budgetRows.length > 0) {
        try {
          await MatterCostCodeBudgetService.saveBudgets(newMatterId, budgetRows);
        } catch (e) {
          console.error('Project created, but cost code budgets could not be saved:', e);
        }
      }
      setCodeBudgets({});
      // Custom table holdings are created by the backend project-create pipeline via selected_custom_tables
      setShowSuccessBanner(true);
      setError('');
      // Reset the form
      setFormData({
        mattername: '',
        mattertype: '',
        projectId: '',
        clientid: '',
        matterid: '',
        employeeid: '',
        opendate: '',
        closedate: '',
        description: '',
        amount: '0',
        budget: '',
        po_number: '',
        workflowid: '',
        country: '',
        application_date: '',
        application_no: '',
        registration_date: '',
        registration_no: '',
        publication_date: '',
        publication_no: '',
        application_status: '',
        priority_data: '',
        office_action_date: '',
        expiry_date: '',
        assoc_employees: [],
        assoc_customfields: [],
      });
      setSelectedClientID('');
      setSelectedTeamMembers([]);
      setTimeout(() => setShowSuccessBanner(false), 3000);
    },
    onError: (error: any) => {
      console.error('Error creating project:', error);
      // HttpService throws { message, status, data }; use error.data for API body
      const body = error?.data ?? error?.response?.data;
      const message =
        body?.error ||
        (body?.errors && typeof body.errors === 'object'
          ? Object.entries(body.errors)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join('; ')
          : null);
      setError(message || 'Failed to create project. Please try again.');
    },
  });

  const handleFilesSelected = (files: File[]) => {
    // Placeholder until backend upload is available
    console.log('Selected files:', files);
  };

  const handleAutoGenerateProjectId = (): void => {
    const year = new Date().getFullYear();
    const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const generatedId = `PRJ-${year}-${randomNum}`;
    setFormData((prev) => ({
      ...prev,
      projectId: generatedId,
    }));
  };

  return (
    <div className="create-projects-page">
      {showSuccessBanner && <div className="successBanner">Project created successfully!</div>}
      {error && <div className="errorBanner">{error}</div>}

      {/* Top header to mirror Create Matters */}
      <div className="create-projects-top-bar">
        <div className="create-projects-top-bar-content">
          <div className="create-projects-header-left">
            <button
              className="create-projects-back-button"
              onClick={() => navigate('/projects')}
              aria-label="Back to projects"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div className="create-projects-header-text">
              <h1 className="create-projects-title">Create New Project</h1>
              <p className="create-projects-subtitle">Set up a new project with all necessary details</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form Container */}
      <div className="create-projects-form-container">
        <div className="create-projects-sections">
          <SectionCard
            title="Required Details"
            subtitle="Essential information needed to create the project"
            className="create-projects-section"
          >
            <div className="create-projects-form-row create-projects-form-row--identifiers">
              <FormInput
                label="Job Number"
                name="application_no"
                type="text"
                placeholder="Enter job number"
                value={formData.application_no}
                onChange={handleInputChange}
              />
              <FormInput
                label="Area"
                name="country"
                type="text"
                placeholder="Enter area"
                value={formData.country}
                onChange={handleInputChange}
              />
              <FormInput
                label="LSD"
                name="registration_no"
                type="text"
                placeholder="Enter LSD"
                value={formData.registration_no}
                onChange={handleInputChange}
              />
              <FormInput
                label="AFE"
                name="publication_no"
                type="text"
                placeholder="Enter AFE"
                value={formData.publication_no}
                onChange={handleInputChange}
              />
            </div>

            <div className="create-projects-form-grid">
              {/* Left Column */}
              <div className="create-projects-form-column">
                <FormInput
                  label="Project Name"
                  name="mattername"
                  type="text"
                  placeholder="Enter project name"
                  value={formData.mattername}
                  onChange={handleInputChange}
                  required
                />

                <div className="form-group form-input-group">
                  <label htmlFor="matterid" className="form-group-label">
                    Final Project ID
                  </label>
                  <div className="matter-id-input-wrapper">
                    <input
                      id="matterid"
                      type="text"
                      name="matterid"
                      className="form-input matter-id-input"
                      placeholder="Project ID + Client ID"
                      value={formData.matterid}
                      readOnly
                    />
                  </div>
                </div>

                <div className="form-group form-input-group">
                  <label htmlFor="projectId" className="form-group-label">
                    Project ID
                    <span className="required-asterisk">*</span>
                  </label>
                  <div className="project-id-input-wrapper">
                    <input
                      id="projectId"
                      type="text"
                      name="projectId"
                      className="form-input project-id-input"
                      placeholder="Enter project ID"
                      value={formData.projectId}
                      onChange={handleInputChange}
                    />
                    <AutoGenerateButton
                      onClick={handleAutoGenerateProjectId}
                      className="project-id-auto-generate"
                    />
                  </div>
                </div>

                <FormSelect
                  label="Assign Leader"
                  name="employeeid"
                  options={employees}
                  value={formData.employeeid}
                  onChange={(value) => setFormData((prev) => ({ ...prev, employeeid: value }))}
                  placeholder="Select leader..."
                  isClearable
                  required
                />

                <div className="form-group form-select-group">
                  <FormSelect
                    label="Add Client"
                    name="clientid"
                    options={clients}
                    value={formData.clientid}
                    onChange={(value) => {
                      setFormData((prev) => ({ ...prev, clientid: value }));
                      setSelectedClientID(value);
                    }}
                    placeholder="Select client..."
                    isClearable
                    isLoading={clientData.isLoading || agentData.isLoading}
                    required
                    formatOptionLabel={(option: SelectOption & { tag?: 'org' | 'individual' }) => (
                      <span className="create-projects-client-option">
                        <span>{option.label}</span>
                        {option.tag && (
                          <span className={`create-projects-client-tag create-projects-client-tag--${option.tag}`}>
                            {option.tag}
                          </span>
                        )}
                      </span>
                    )}
                  />
                  <button
                    type="button"
                    className="create-new-link"
                    onClick={() => navigate('/createClient')}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Create New
                  </button>
                </div>
              </div>

              {/* Right Column */}
              <div className="create-projects-form-column">
                <FormInput
                  label="Creation Date"
                  name="opendate"
                  type="date"
                  placeholder="mm/dd/yyyy"
                  value={formData.opendate}
                  onChange={handleInputChange}
                  required
                />

                <FormInput
                  label="Due Date"
                  name="closedate"
                  type="date"
                  placeholder="mm/dd/yyyy"
                  value={formData.closedate}
                  onChange={handleInputChange}
                  required
                />

                <FormInput
                  label="PO Number"
                  name="po_number"
                  placeholder="Enter PO number"
                  value={formData.po_number}
                  onChange={handleInputChange}
                  maxLength={255}
                />

                {/* The overall project budget is the sum of the per-cost-code budgets below. */}
                <div className="form-group">
                  <label className="form-group-label">Cost Code Budgets (hrs)</label>
                  <CostCodeBudgetInputs
                    values={codeBudgets}
                    onChange={(code, value) =>
                      setCodeBudgets((prev) => ({ ...prev, [code]: value }))
                    }
                  />
                </div>

                <div className="form-group form-select-group">
                  <FormSelect
                    label="Add Workflow Template"
                    name="workflowid"
                    options={templates.map((tpl) => ({
                      value: tpl.templateId,
                      label: tpl.name,
                    }))}
                    value={formData.workflowid}
                    onChange={(value) => setFormData((prev) => ({ ...prev, workflowid: value }))}
                    placeholder="Select workflow template..."
                    isClearable
                    required
                  />
                  <button
                    type="button"
                    className="create-new-link"
                    onClick={() => navigate('/templates')}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Create New
                  </button>
                </div>
              </div>
            </div>

            <div className="create-projects-form-actions">
              <button
                type="button"
                className="create-projects-submit-button"
                onClick={handleSubmit}
              >
                Create Project
              </button>
            </div>
          </SectionCard>

          <SectionCard
            title="Tables"
            subtitle="Add custom fields and tables to your project"
            className="create-projects-section"
          >
            <CustomFieldsTablesPanel
              title=""
              customTables={(Array.isArray(customTables) ? customTables : []).map((t) => ({
                id: t.id,
                name: t.table_name,
                description: 'Table for detailed configuration',
              }))}
              customFields={[]}
              selectedTableIds={selectedCustomTables}
              selectedFieldIds={selectedFieldIds}
              onToggleTable={handleCustomTableClick}
              onToggleField={(fieldId) => toggleCustomFieldSelection(fieldId)}
              onAddGlobalTable={() => setShowCustomTablesModal(true)}
            />
          </SectionCard>

          <SectionCard
            title="Optional & Customization"
            subtitle="Additional details and custom configurations"
            className="create-projects-section"
          >
            <div className="create-projects-optional-stack">
              <FormEntryMultilineTextbox
                label="Description/Details"
                name="description"
                placeholder="Enter project description, objectives, and key details..."
                value={formData.description}
                onChange={handleInputChange}
              />

              <TeamMembersPicker
                options={employees}
                selectedIds={selectedTeamMembers}
                onRemove={(id) =>
                  setSelectedTeamMembers((prev) => prev.filter((empId) => empId !== id))
                }
                onRequestAdd={handleAddTeamMembersClick}
              />

              <FileDropzone onFilesSelected={handleFilesSelected} />
            </div>
          </SectionCard>
        </div>
      </div>

      {/* Modals and Overlays */}
      {showMoreInfoModal && (
        <div className="create-modal-overlay" onClick={toggleModals}>
          <div
            className="create-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="create-modal-header">
              <h2>Add Additional Information</h2>
              <button className="create-modal-close" onClick={toggleModals}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form>
              <div className="m-form-group">
                <label className="m-form-group-label">Job Number</label>
                <div className="m-input-icon">
                  <input
                    type="text"
                    name="application_no"
                    value={formData.application_no}
                    onChange={handleInputChange}
                    placeholder="Enter job number"
                  />
                </div>
              </div>

              <div className="m-form-group">
                <label className="m-form-group-label">Area</label>
                <div className="m-input-icon">
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Enter area"
                  />
                </div>
              </div>

              <div className="m-form-group">
                <label className="m-form-group-label">LSD</label>
                <div className="m-input-icon">
                  <input
                    type="text"
                    name="registration_no"
                    value={formData.registration_no}
                    onChange={handleInputChange}
                    placeholder="Enter LSD"
                  />
                </div>
              </div>

              <div className="m-form-group">
                <label className="m-form-group-label">AFE</label>
                <div className="m-input-icon">
                  <input
                    type="text"
                    name="publication_no"
                    value={formData.publication_no}
                    onChange={handleInputChange}
                    placeholder="Enter AFE"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showWorkflows && (
        <SelectModal
          isOpen={showWorkflows}
          onClose={toggleWorkflowModal}
          title="Available Templates"
          items={templates.map((template) => ({
            id: template.templateId,
            name: template.name,
            description: template.description || undefined,
            steps: templateStepsById[template.templateId] || [],
          }))}
          selectedItemId={formData.workflowid}
          onItemSelect={handleWorkflowClick}
          loading={loadingWorkflows}
          emptyMessage="No templates available."
          renderItem={(item: { id: string; name: string; description?: string; steps?: TemplateStepSummary[] }) => (
            <div>
              <strong>{item.name}</strong>
              {item.description && <p>{item.description}</p>}
              <div style={{ marginTop: '0.5rem' }}>
                {item.steps && item.steps.length ? (
                  <ul style={{ paddingLeft: '1.2rem', margin: 0 }}>
                    {item.steps.map((step) => (
                      <li key={step.stepId} style={{ marginBottom: '0.25rem' }}>
                        {step.stepOrder}. {step.name}
                        {step.discipline ? ` — ${step.discipline}` : ''}
                        {step.offsetDays != null ? ` (offset: ${step.offsetDays}d)` : ''}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: 0 }}>No steps defined.</p>
                )}
              </div>
            </div>
          )}
        />
      )}

      {showEmployeeModal && (
        <div
          className="create-modal-overlay"
          onClick={handleAddTeamMembersClick}
        >
          <div
            className="CM-addTM-popout"
            onClick={(e) => e.stopPropagation()} // Prevent closing on modal click
          >
            <h3>Select Team Members</h3>
            <Select
              isMulti
              options={employees} // Use the employees list for options
              onChange={(selectedOptions) => {
                setSelectedTeamMembers(
                  selectedOptions
                    ? selectedOptions.map((option) => option.value)
                    : []
                );
              }}
              value={employees.filter((emp) =>
                selectedTeamMembers.includes(emp.value)
              )}
              placeholder="Select team members..."
            />
          </div>
        </div>
      )}

      {/* Custom Fields Modal */}
      {showCustomFieldsModal && (
        <div
          className="create-modal-overlay"
          onClick={handleCustomFieldsClick}
        >
          <div
            className="CM-custom-fields-popout"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Select Custom Fields</h3>
              <div className="modal-actions">
                <button
                  className="toggle-view-button"
                  onClick={() => setShowGroupRules(!showGroupRules)}
                >
                  {showGroupRules ? 'View Fields' : 'View Groups'}
                  <FontAwesomeIcon
                    icon={showGroupRules ? faLayerGroup : faChevronDown}
                  />
                </button>
                <button
                  className="close-modal"
                  onClick={handleCustomFieldsClick}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            {showGroupRules ? (
              <div className="group-rules-list">
                {groupRules.length === 0 ? (
                  <p className="no-groups-message">No groups available.</p>
                ) : (
                  <ul>
                    {groupRules.map((group) => (
                      <li
                        key={group.id}
                        className={`group-rule-item ${
                          selectedGroupRule?.id === group.id ? 'selected' : ''
                        }`}
                        onClick={() => handleGroupRuleSelect(group)}
                      >
                        <div className="group-header">
                          <h4>{group.group_name}</h4>
                        </div>
                        <div className="group-fields">
                          {Array.isArray(group.assoc_customfields)
                            ? group.assoc_customfields.map((fieldId) => {
                                const field = customFields.find(
                                  (f) => f.customfield_id === fieldId
                                );
                                return field ? (
                                  <span
                                    key={fieldId}
                                    className="group-field-tag"
                                  >
                                    {field.custom_field}
                                  </span>
                                ) : null;
                              })
                            : Object.keys(group.assoc_customfields).map(
                                (fieldId) => {
                                  const field = customFields.find(
                                    (f) =>
                                      f.customfield_id === parseInt(fieldId)
                                  );
                                  return field ? (
                                    <span
                                      key={fieldId}
                                      className="group-field-tag"
                                    >
                                      {field.custom_field}
                                    </span>
                                  ) : null;
                                }
                              )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="custom-fields-list">
                {customFields.length === 0 ? (
                  <p className="no-fields-message">
                    No custom fields available.
                  </p>
                ) : (
                  <ul>
                    {customFields.map((field) => (
                      <li
                        key={field.customfield_id}
                        className="custom-field-item"
                      >
                        <div className="field-checkbox">
                          <input
                            type="checkbox"
                            checked={
                              !!selectedCustomFields[field.customfield_id]
                            }
                            onChange={() =>
                              toggleCustomFieldSelection(field.customfield_id)
                            }
                          />
                        </div>
                        <div className="field-content">
                          <p>{field.custom_field}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showCustomTablesModal && (
        <div
          className="create-modal-overlay"
          onClick={() => setShowCustomTablesModal(false)}
          style={{ zIndex: 10001 }}
        >
          <div
            className="template-edit-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="template-edit-modal-header">
              <h2>Manage Custom Tables</h2>
              <button
                className="template-edit-modal-close"
                onClick={() => {
                  setShowCustomTablesModal(false);
                  // Refresh tables when closing to get any newly created ones
                  fetchCustomTables();
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="template-edit-modal-content">
              <TemplateEdit permishId={null} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateProjectsPage;
